/**
 * Redux slice (src/store/slices/teacherSessionSlice.js).
 *
 * Data flow (typical):
 * - UI dispatches actions/thunks from `src/store/actions/*`.
 * - `reducers` handles synchronous state updates.
 * - `extraReducers` reacts to thunk pending/fulfilled/rejected actions.
 */

import { createSlice } from "@reduxjs/toolkit";
import { 
  teacherJoinSession, 
  getTeacherSessionStatus,
  studentJoinSession,
  getStudentSessionStatus 
} from "./../actions/teacherSessionActions";

const initialTeacherState = {
  loading: false,
  sessionActive: false,
  sessionDetails: null,
  error: null,
};

const initialStudentState = {
  loading: false,
  sessionActive: false,
  sessionDetails: null,
  error: null,
};

/**
 * RTK slice definition: teacherSessionSlice.
 */
const teacherSessionSlice = createSlice({
  name: "teacherSession",
  initialState: initialTeacherState,
  reducers: {},
  /**
   * Reducer: extraReducers.
   */
  extraReducers: (builder) => {
    builder
      // extraReducers handler: teacherJoinSession.pending
      .addCase(teacherJoinSession.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      // extraReducers handler: teacherJoinSession.fulfilled
      .addCase(teacherJoinSession.fulfilled, (state, action) => {
        state.loading = false;
        // Launch authorization is not evidence of a connected participant.
        if (action.meta?.arg?.launchOnly === true) return;
        state.sessionActive = true;
        state.sessionDetails = action.payload;
      })
      // extraReducers handler: teacherJoinSession.rejected
      .addCase(teacherJoinSession.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // extraReducers handler: getTeacherSessionStatus.pending
      .addCase(getTeacherSessionStatus.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      // extraReducers handler: getTeacherSessionStatus.fulfilled
      .addCase(getTeacherSessionStatus.fulfilled, (state, action) => {
        state.loading = false;
        state.sessionActive = action.payload.isActive;
        state.sessionDetails = action.payload.sessionDetails;
      })
      // extraReducers handler: getTeacherSessionStatus.rejected
      .addCase(getTeacherSessionStatus.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

/**
 * RTK slice definition: studentSessionSlice.
 */
  const studentSessionSlice = createSlice({
  name: "studentSession",
  initialState: initialStudentState,
  reducers: {},
  /**
   * Reducer: extraReducers.
   */
  extraReducers: (builder) => {
    builder
      // extraReducers handler: studentJoinSession.pending
      .addCase(studentJoinSession.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      // extraReducers handler: studentJoinSession.fulfilled
      .addCase(studentJoinSession.fulfilled, (state, action) => {
        state.loading = false;
        if (action.meta?.arg?.launchOnly === true) return;
        state.sessionActive = true;
        state.sessionDetails = action.payload;
      })
      // extraReducers handler: studentJoinSession.rejected
      .addCase(studentJoinSession.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // extraReducers handler: getStudentSessionStatus.pending
      .addCase(getStudentSessionStatus.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      // extraReducers handler: getStudentSessionStatus.fulfilled
      .addCase(getStudentSessionStatus.fulfilled, (state, action) => {
        state.loading = false;
        state.sessionActive = action.payload.isActive;
        state.sessionDetails = action.payload.sessionDetails;
      })
      // extraReducers handler: getStudentSessionStatus.rejected
      .addCase(getStudentSessionStatus.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const teacherSessionReducer = teacherSessionSlice.reducer;
export const studentSessionReducer = studentSessionSlice.reducer;
